import { useEffect, useMemo, useState } from "react";

import { useNavigate, useParams } from "react-router-dom";

import PhotoGallery from "../components/PhotoGallery";

import AmenitiesGrid from "../components/AmenitiesGrid";

import BookingCard from "../components/BookingCard";

import { deleteSalon, getSalonBooths, getSalonById } from "../api/salons";
import { resolveAssetUrl } from "../utils/assetUrl";



const fallbackRules = ["Respect quiet hours", "No smoking", "Return space as you found it"];

const thumbStyle = {

  width: "120px",

  height: "120px",

  borderRadius: "8px",

  objectFit: "cover",

  marginRight: "10px",

};



function SalonDetail() {

  const { id } = useParams();

  const navigate = useNavigate();

  const [salon, setSalon] = useState(null);

  const [booths, setBooths] = useState([]);

  const [loading, setLoading] = useState(true);

  const [boothLoading, setBoothLoading] = useState(true);

  const [error, setError] = useState("");

  const [actionError, setActionError] = useState("");



  useEffect(() => {

    setLoading(true);

    getSalonById(id)

      .then((res) => setSalon(res.data || res))

      .catch(() => setError("Unable to load salon details."))

      .finally(() => setLoading(false));

  }, [id]);



  useEffect(() => {

    setBoothLoading(true);

    getSalonBooths(id)

      .then((data) => setBooths(data || []))

      .catch(() => setBooths([]))

      .finally(() => setBoothLoading(false));

  }, [id]);



  const locationLabel = useMemo(

    () => [salon?.city, salon?.province].filter(Boolean).join(", "),

    [salon?.city, salon?.province]

  );



  const mapLink = useMemo(() => {

    if (!salon?.latitude || !salon?.longitude) return null;

    return `https://www.google.com/maps?q=${salon.latitude},${salon.longitude}`;

  }, [salon?.latitude, salon?.longitude]);



  const minPrice = useMemo(() => {

    const prices = booths

      .map((b) => b.price_per_hour || b.price || 0)

      .filter((p) => typeof p === "number" && p > 0);

    if (!prices.length) return null;

    return Math.min(...prices);

  }, [booths]);



  const featuredBooth = useMemo(() => {

    if (!booths.length) return null;

    return booths.find((b) => b.is_available) || booths[0];

  }, [booths]);



  const ratingDisplay = useMemo(() => {

    if (!salon) return "4.9";

    if (typeof salon.rating === "number") return salon.rating.toFixed(1);

    return salon.rating || "4.9";

  }, [salon]);



  const handleBook = () => {

    if (featuredBooth) {

      navigate(`/booths/${featuredBooth.id}/checkout`);

      return;

    }

    window.scrollTo({ top: 0, behavior: "smooth" });

  };



  const currentUserId =

    typeof window !== "undefined" ? localStorage.getItem("user_id") : null;

  const currentRole =

    typeof window !== "undefined"

      ? (localStorage.getItem("role") || "").toLowerCase()

      : "";

  const canManage =

    currentRole === "admin" ||

    (currentRole === "host" &&

      currentUserId &&

      salon?.owner_id &&

      String(salon.owner_id) === String(currentUserId));



  const handleDelete = async () => {

    const confirmed = window.confirm(

      "Are you sure? This will remove all associated booths."

    );

    if (!confirmed) return;

    try {

      await deleteSalon(id);

      navigate("/my-salons");

    } catch (err) {

      console.error(err);

      setActionError("Failed to delete salon.");

    }

  };



  if (loading) {

    return (

      <div className="p-10 text-center text-gray-500">

        Loading salon...

      </div>

    );

  }



  if (error || !salon) {

    return (

      <div className="p-10 text-center text-red-600">

        {error || "Salon not found."}

      </div>

    );

  }



  return (

    <div className="bg-slate-50 min-h-screen">

      <div className="max-w-6xl mx-auto px-4 py-8 md:py-12">

        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">

          <div>

            <p className="text-sm uppercase tracking-[0.2em] text-emerald-600 font-semibold">

              Salon

            </p>

            <h1 className="text-3xl md:text-4xl font-semibold text-slate-900 mt-1">

              {salon.name}

            </h1>

            <p className="text-slate-600 flex items-center gap-3 mt-2">

              <span>

                {locationLabel ||

                  salon.address ||

                  "Location coming soon"}

              </span>

              <span className="inline-flex items-center gap-1 text-amber-600 font-semibold">

                ★ {ratingDisplay}{" "}

                <span className="text-gray-500 font-normal">

                  ({salon.review_count || 0} reviews)

                </span>

              </span>

            </p>

          </div>

          <div className="text-right text-sm text-slate-600">

            <p className="text-emerald-600 font-semibold">

              Ellyanna Verified

            </p>

            <p className="text-2xl font-semibold text-slate-900">

              {minPrice ? `$${minPrice}` : "Pricing available"}

              {minPrice && (

                <span className="text-sm font-normal text-slate-500">

                  {" "}

                  / hour

                </span>

              )}

            </p>

          </div>

        </div>



        {actionError && (

          <div className="text-red-600 text-sm mt-2">

            {actionError}

          </div>

        )}

        {canManage && (

          <div className="flex flex-wrap gap-3 mt-3">

            <button

              onClick={() => navigate(`/salons/${id}/edit`)}

              className="px-3 py-2 rounded-lg bg-blue-600 text-white text-sm"

            >

              Edit salon

            </button>

            <button

              onClick={handleDelete}

              className="px-3 py-2 rounded-lg bg-rose-600 text-white text-sm"

            >

              Delete salon

            </button>

          </div>

        )}



        <div className="mt-6">

          <PhotoGallery photos={salon.photos} />

          {salon.photos?.length > 0 && (

            <div

              style={{

                display: "flex",

                gap: "10px",

                marginTop: "12px",

                marginBottom: "8px",

                overflowX: "auto",

              }}

            >

              {salon.photos.map((url) => (

                <img

                  key={url}

                  src={resolveAssetUrl(url)}

                  alt="salon"

                  style={thumbStyle}

                />

              ))}

            </div>

          )}

        </div>



        <div className="mt-10 grid grid-cols-1 lg:grid-cols-3 gap-8">

          <div className="lg:col-span-2 space-y-8">

            <section className="rounded-2xl bg-white p-6 shadow-sm border border-slate-100">

              <div className="flex items-center justify-between mb-3">

                <div>

                  <p className="text-sm text-emerald-600 font-semibold">

                    About this listing

                  </p>

                  <h2 className="text-xl font-semibold text-slate-900">

                    Elevated salon workspace

                  </h2>

                </div>

                <span className="text-xs px-3 py-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-100">

                  Instant book

                </span>

              </div>

              <p className="text-slate-700 leading-relaxed">

                {salon.description ||

                  "Thoughtfully designed salon space with flexible booths and suites so you can welcome clients with confidence."}

              </p>

            </section>



            <section className="rounded-2xl bg-white p-6 shadow-sm border border-slate-100">

              <div className="flex items-center justify-between mb-4">

                <h2 className="text-xl font-semibold text-slate-900">

                  Amenities

                </h2>

                <span className="text-sm text-slate-500">

                  {(salon.amenities || []).length} included

                </span>

              </div>

              <AmenitiesGrid amenities={salon.amenities} />

            </section>



            <section className="rounded-2xl bg-white p-6 shadow-sm border border-slate-100">

              <h2 className="text-xl font-semibold mb-3 text-slate-900">

                House rules

              </h2>

              <div className="space-y-2 text-slate-700">

                {(salon.rules && salon.rules.length > 0

                  ? salon.rules

                  : fallbackRules

                ).map((rule) => (

                  <div key={rule} className="flex items-start gap-2">

                    <span className="text-emerald-500 mt-1">●</span>

                    <span>{rule}</span>

                  </div>

                ))}

              </div>

            </section>



            <section className="rounded-2xl bg-white p-6 shadow-sm border border-slate-100">

              <div className="flex items-center justify-between mb-3">

                <h2 className="text-xl font-semibold text-slate-900">

                  Where you'll be

                </h2>

                {mapLink && (

                  <a

                    href={mapLink}

                    target="_blank"

                    rel="noreferrer"

                    className="text-emerald-600 text-sm hover:underline"

                  >

                    Open map

                  </a>

                )}

              </div>

              <div className="relative overflow-hidden rounded-xl bg-gradient-to-br from-emerald-50 via-white to-emerald-100 p-6 border border-emerald-100">

                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">

                  <div>

                    <p className="text-slate-800 font-semibold">

                      {salon.address ||

                        "Exact address shared after booking"}

                    </p>

                    <p className="text-slate-600">{locationLabel}</p>

                    {salon.postal_code && (

                      <p className="text-slate-600">

                        {salon.postal_code}

                      </p>

                    )}

                  </div>

                  {salon.latitude && salon.longitude ? (

                    <div className="rounded-lg bg-white px-4 py-2 shadow-md text-sm text-slate-700">

                      Lat: {salon.latitude}, Lng: {salon.longitude}

                    </div>

                  ) : (

                    <div className="rounded-lg bg-white px-4 py-2 shadow-md text-sm text-slate-500">

                      Coordinates available upon request

                    </div>

                  )}

                </div>

              </div>

            </section>



            {/* Booths list including add-on amenities preview */}

            <section className="rounded-2xl bg-white p-6 shadow-sm border border-slate-100">

              <div className="flex items-center justify-between mb-4">

                <h2 className="text-xl font-semibold text-slate-900">

                  Booths in this salon

                </h2>

                <span className="text-sm text-slate-500">

                  {boothLoading

                    ? "Loading..."

                    : `${booths.length} options`}

                </span>

              </div>

              {boothLoading && (

                <p className="text-slate-600">Loading booths...</p>

              )}

              {!boothLoading && booths.length === 0 && (

                <p className="text-slate-600">

                  No booths are listed yet. Check back soon.

                </p>

              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

                {booths.map((booth) => {

                  const addons =

                    booth.amenity_addons ||

                    booth.amenityAddons ||

                    [];

                  const hasAddons =

                    Array.isArray(addons) && addons.length > 0;

                  const addonLabels = hasAddons

                    ? addons

                        .map((a) => a?.label || a?.key)

                        .filter(Boolean)

                    : [];



                  return (

                    <div

                      key={booth.id}

                      className="border border-slate-200 rounded-xl p-4 shadow-sm bg-white flex flex-col gap-3"

                    >

                      <div className="space-y-2">

                        <p className="text-sm text-slate-500">

                          Booth {booth.booth_number || booth.id}

                        </p>

                        <h3 className="text-lg font-semibold text-slate-900">

                          {booth.booth_name || booth.name}

                        </h3>

                        <p className="text-slate-700">

                          {booth.description ||

                            "Ready for your guests."}

                        </p>

                        <p className="text-slate-900 font-semibold">

                          ${booth.price_per_hour || booth.price} / hour

                        </p>

                        <p

                          className={`text-sm ${

                            booth.is_available === false

                              ? "text-rose-600"

                              : "text-emerald-600"

                          }`}

                        >

                          {booth.is_available === false

                            ? "Unavailable"

                            : "Available"}

                        </p>



                        {hasAddons && (

                          <div className="mt-1 text-xs text-slate-700">

                            <span className="font-semibold">

                              Add-on amenities:

                            </span>{" "}

                            {addonLabels.join(", ")}

                          </div>

                        )}

                      </div>

                      <button

                        onClick={() =>

                          navigate(`/booths/${booth.id}/checkout`)

                        }

                        className="rounded-lg border border-slate-300 px-3 py-2 text-sm font-semibold text-slate-800 hover:border-emerald-400 transition"

                      >

                        Book this booth

                      </button>

                    </div>

                  );

                })}

              </div>

            </section>



            <section className="rounded-2xl bg-white p-6 shadow-sm border border-slate-100">

              <div className="flex items-center justify-between mb-4">

                <div>

                  <p className="text-sm text-emerald-600 font-semibold">

                    Reviews

                  </p>

                  <h2 className="text-xl font-semibold text-slate-900">

                    What stylists are saying

                  </h2>

                </div>

                <span className="text-sm text-slate-500">

                  Coming soon

                </span>

              </div>

              <div className="rounded-xl border border-dashed border-slate-200 p-6 text-slate-600 bg-slate-50">

                Reviews and ratings will appear here once stylists

                complete their bookings.

              </div>

            </section>

          </div>



          <div className="lg:col-span-1">

            <BookingCard

              price={

                featuredBooth?.price_per_hour ||

                featuredBooth?.price ||

                minPrice ||

                0

              }

              rating={Number(ratingDisplay) || 0}

              reviewCount={salon.review_count || 0}

              location={locationLabel}

              boothId={featuredBooth?.id}

              onBook={handleBook}

              note="Pick your booth, schedule, and add-on amenities on the next step."

            />

            <div className="mt-4 rounded-xl border border-emerald-100 bg-emerald-50 p-4 text-sm text-emerald-800">

              Ellyanna Verified means the salon meets our quality checklist

              for equipment, cleanliness, and safety.

            </div>

          </div>

        </div>

      </div>

    </div>

  );

}



export default SalonDetail;

